// Chemin: C:\gestion-copro\src\services\inscription.service.ts
// Service pour gérer les inscriptions des copropriétaires et vigies
// Session 8 : auto-inscription avec validation manuelle
// Session 101 : ajout registerVigie() — inscription autonome formule VIGIE
// Session 139 : registerVigie() → statut en_attente_paiement + retour coproprieteId pour Stripe
// Session 163 : REFONTE — registerVigie() ne crée plus de compte Auth
//   Le compte Auth est créé par le webhook Stripe après paiement réussi.
//   Élimine les comptes fantômes et les problèmes "email déjà utilisé".

import { 
  createUserWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  getDocs, 
  updateDoc, 
  query, 
  where,
  orderBy,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { UserProfile, Lot, PendingUser } from '../types/user.types';

// ============================================
// VALIDATION GSM
// ============================================

const GSM_REGEX = /^(?:(?:\+33|0033|0)[67])(?:[0-9]{8})$/;

export const validateGSM = (gsm: string): boolean => {
  const cleaned = gsm.replace(/[\s.-]/g, '');
  return GSM_REGEX.test(cleaned);
};

export const formatGSM = (gsm: string): string => {
  return gsm.replace(/[\s.-]/g, '');
};

// ============================================
// INSCRIPTION COPROPRIÉTAIRE (existant — inchangé)
// ============================================

interface RegisterResult {
  success: boolean;
  error?: string;
}

export const registerCoproprietaire = async (
  email: string,
  password: string,
  nom: string,
  prenom: string,
  gsm: string,
  etage: number,
  lots: Lot[]
): Promise<RegisterResult> => {
  try {
    // Créer le compte Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const uid = userCredential.user.uid;

    // Créer le document utilisateur dans Firestore
    const now = Timestamp.now();
    const userData: Omit<UserProfile, 'id'> = {
      email,
      nom,
      prenom,
      gsm: formatGSM(gsm),
      etage,
      lots,
      role: 'coproprietaire',
      status: 'pending',
      preferences: {
        notifications: true,
        emailRecap: true,
        langue: 'fr'
      },
      lastLogin: now,
      createdAt: now,
      updatedAt: now
    };

    await setDoc(doc(db, 'users', uid), userData);

    // Déconnecter immédiatement (compte en attente)
    await signOut(auth);

    return { success: true };
  } catch (error: any) {
    console.error('Erreur inscription:', error);
    
    let message = 'Erreur lors de l\'inscription';
    if (error.code === 'auth/email-already-in-use') {
      message = 'Cette adresse email est déjà utilisée';
    } else if (error.code === 'auth/weak-password') {
      message = 'Le mot de passe doit contenir au moins 6 caractères';
    } else if (error.code === 'auth/invalid-email') {
      message = 'Adresse email invalide';
    }
    
    return { success: false, error: message };
  }
};

// ============================================
// INSCRIPTION VIGIE — Session 163 : REFONTE
// ============================================
// Nouveau parcours :
//   1. Données stockées dans inscriptions_pending (PAS de compte Auth)
//   2. Redirection Stripe Checkout (sans authentification)
//   3. Webhook checkout.session.completed → crée compte Auth + Firestore + quotas
//
// Avantages :
//   - Zéro compte fantôme si abandon paiement
//   - Zéro "email déjà utilisé" au 2e essai
//   - L'email de bienvenue part via Mailjet (pas Firebase = pas de spam)

interface RegisterVigieData {
  email: string;
  password: string;
  nom: string;
  prenom: string;
  nomCopropriete: string;
  adresseCopropriete: string;
  nombreLots?: number;
  nomSyndic?: string;
}

interface RegisterVigieResult {
  success: boolean;
  inscriptionId?: string;
  error?: string;
}

export const registerVigie = async (data: RegisterVigieData): Promise<RegisterVigieResult> => {
  try {
    const { email, password, nom, prenom, nomCopropriete, adresseCopropriete, nombreLots, nomSyndic } = data;

    const normalizedEmail = email.trim().toLowerCase();

    // Vérifier que l'email n'a pas déjà une inscription pending non expirée
    const existingQuery = query(
      collection(db, 'inscriptions_pending'),
      where('email', '==', normalizedEmail),
      where('status', '==', 'pending')
    );
    const existingSnap = await getDocs(existingQuery);

    // Si une inscription pending existe, la réutiliser (l'utilisateur retente)
    if (!existingSnap.empty) {
      const existingDoc = existingSnap.docs[0];
      // Mettre à jour avec les nouvelles données (l'utilisateur a peut-être changé son mot de passe)
      await updateDoc(existingDoc.ref, {
        password,
        nom,
        prenom,
        nomCopropriete,
        adresseCopropriete,
        nombreLots: nombreLots || null,
        nomSyndic: nomSyndic || null,
        updatedAt: Timestamp.now(),
      });
      return { success: true, inscriptionId: existingDoc.id };
    }

    const now = Timestamp.now();

    // Stocker les données d'inscription (y compris le mot de passe en clair)
    // Le mot de passe sera utilisé par le webhook pour créer le compte Auth
    // puis supprimé immédiatement après
    const inscriptionData = {
      email: normalizedEmail,
      password, // Temporaire — supprimé par le webhook après création du compte Auth
      nom,
      prenom,
      nomCopropriete,
      adresseCopropriete,
      nombreLots: nombreLots || null,
      nomSyndic: nomSyndic || null,
      status: 'pending' as const,
      createdAt: now,
      updatedAt: now,
    };

    const inscriptionRef = await addDoc(collection(db, 'inscriptions_pending'), inscriptionData);

    return { success: true, inscriptionId: inscriptionRef.id };

  } catch (error: any) {
    console.error('Erreur inscription vigie:', error);
    return { success: false, error: 'Erreur lors de l\'inscription. Veuillez réessayer.' };
  }
};

// ============================================
// GESTION DES UTILISATEURS EN ATTENTE
// ============================================

export const getPendingUsers = async (): Promise<PendingUser[]> => {
  try {
    const q = query(
      collection(db, 'users'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      email: doc.data().email,
      nom: doc.data().nom,
      prenom: doc.data().prenom,
      gsm: doc.data().gsm,
      etage: doc.data().etage,
      lots: doc.data().lots || [],
      createdAt: doc.data().createdAt
    }));
  } catch (error) {
    console.error('Erreur chargement utilisateurs en attente:', error);
    return [];
  }
};

export const validateUser = async (
  userId: string, 
  validatedBy: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      status: 'active',
      validatedAt: Timestamp.now(),
      validatedBy,
      updatedAt: Timestamp.now()
    });
    return { success: true };
  } catch (error) {
    console.error('Erreur validation:', error);
    return { success: false, error: 'Erreur lors de la validation' };
  }
};

export const rejectUser = async (
  userId: string, 
  rejectedBy: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      status: 'rejected',
      rejectedAt: Timestamp.now(),
      rejectedBy,
      rejectionReason: reason || '',
      updatedAt: Timestamp.now()
    });
    return { success: true };
  } catch (error) {
    console.error('Erreur refus:', error);
    return { success: false, error: 'Erreur lors du refus' };
  }
};

// ============================================
// UTILITAIRES
// ============================================

export const getUserById = async (userId: string): Promise<UserProfile | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return null;
    return { id: userDoc.id, ...userDoc.data() } as UserProfile;
  } catch (error) {
    console.error('Erreur lecture utilisateur:', error);
    return null;
  }
};

export const isUserActive = async (userId: string): Promise<boolean> => {
  const user = await getUserById(userId);
  return user?.status === 'active';
};
